import { useTranslation } from "react-i18next";
import { useState, useMemo, useCallback, useContext, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";

import { UserContext } from "../../../hooks/UserContext";
import {
    PageView,
    Notification,
    ConfirmDialog,
    realCarrierName
} from "../../../components";
import { Loader } from "../../../components/Loader";
import { Filter } from "../../../components/NewTable/modules/Filter";
import {
    fetchLastmileShippingMethods,
    updateLastmileShippingMethod,
    fetchCarrierNames
} from "../../../services";
import { fetchWarehouse } from "../../../services/warehouseSevices";
import { LASTMILE_SHIPPING_METHOD_COVERAGES } from "../../../navigation/constants";
import { ShippingMethodHistoryModal } from "./ShippingMethodHistoryModal";
import { ClockIcon, BanIcon, CheckCircleIcon, ChevronRightIcon } from "@heroicons/react/outline";

const T_PREFIX = 'lastmile_management.shipping_methods';

// Carrier badge: deterministic color from the carrier name (full class strings so
// Tailwind's purge keeps them). White initials on a solid brand-ish tile.
const CARRIER_BADGE_COLORS = [
    'bg-cubbo-700',
    'bg-red-500',
    'bg-blue-600',
    'bg-emerald-600',
    'bg-purple-600',
    'bg-pink-600',
    'bg-indigo-600',
    'bg-green-600'
];

const carrierBadgeColor = (carrier = '') => {
    let hash = 0;
    for (let i = 0; i < carrier.length; i++) {
        hash = (hash + carrier.charCodeAt(i)) % CARRIER_BADGE_COLORS.length;
    }
    return CARRIER_BADGE_COLORS[hash];
};

const carrierInitials = (carrier = '') => {
    const letters = carrier.replace(/[^a-zA-Z]/g, '');
    return (letters.slice(0, 3) || carrier.slice(0, 3) || '?').toUpperCase();
};

export const ListShippingMethodsContainer = () => {
    const { i18n } = useTranslation();
    const { user } = useContext(UserContext);
    const { warehouse_id } = useParams();
    const history = useHistory();
    const queryClient = useQueryClient();

    // Permissions
    const hasAccess = !!user?.permissions?.index_shipping_methods_management;
    const canEdit = !!user?.permissions?.update_shipping_methods_management;

    // State (no page/per_page — fetch loops through all pages internally)
    const [searchParams, setSearchParams] = useState({ warehouse_id });
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [selectedHistoryMethod, setSelectedHistoryMethod] = useState(null);
    const [notification, setNotification] = useState({ show: false, type: '', title: '' });
    const [confirmToggleOpen, setConfirmToggleOpen] = useState(false);
    const [methodToToggle, setMethodToToggle] = useState(null);

    // Queries
    const { data: warehouse } = useQuery(
        ['warehouse', warehouse_id],
        () => fetchWarehouse(warehouse_id),
        { keepPreviousData: true, refetchOnWindowFocus: false }
    );

    const { data: carrierNamesData } = useQuery(
        ['lastmile_carrier_names'],
        () => fetchCarrierNames(),
        { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, enabled: hasAccess }
    );

    const carrierNameOptions = useMemo(() => {
        const raw = carrierNamesData?.carrier_names
            ?? carrierNamesData?.carriers
            ?? (Array.isArray(carrierNamesData) ? carrierNamesData : []);
        const names = (Array.isArray(raw) ? raw : [])
            .map(x => typeof x === 'string' ? x : (x?.name || x?.carrier_name || x?.label || x?.id))
            .filter(Boolean);
        return Array.from(new Set(names))
            .sort((a, b) => a.localeCompare(b))
            .map(name => ({ id: name, label: name }));
    }, [carrierNamesData]);

    // Fetch all methods across pages so they can be grouped by carrier.
    // The lastmile API caps per_page (1000 returns 422), so page through at 200.
    const fetchAllMethods = useCallback(async () => {
        const perPage = 200;
        const maxPages = 10;
        const firstPage = await fetchLastmileShippingMethods({ ...searchParams, page: 1, per_page: perPage });
        const allItems = [...(firstPage.shipping_methods || [])];
        const totalPages = firstPage.meta?.total_pages || 1;
        const pagesToFetch = Math.min(totalPages, maxPages);
        if (totalPages > maxPages) {
            console.warn(`Shipping methods truncated: ${totalPages} pages available, capped at ${maxPages}`);
        }
        if (pagesToFetch > 1) {
            const pages = Array.from({ length: pagesToFetch - 1 }, (_, i) => i + 2);
            const results = await Promise.allSettled(
                pages.map(p => fetchLastmileShippingMethods({ ...searchParams, page: p, per_page: perPage }))
            );
            results.forEach(r => {
                if (r.status === 'fulfilled') allItems.push(...(r.value.shipping_methods || []));
            });
        }
        return { shipping_methods: allItems };
    }, [searchParams]);

    const { isLoading, isError, error, data } = useQuery(
        ['lastmile_shipping_methods', searchParams],
        fetchAllMethods,
        { keepPreviousData: true, refetchOnWindowFocus: false, enabled: hasAccess }
    );

    // Mutations
    const toggleActiveMutation = useMutation(
        ({ id, is_active }) => updateLastmileShippingMethod(id, { is_active }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['lastmile_shipping_methods']);
                showNotification('success', i18n.t(`${T_PREFIX}.notifications.update_success`));
            },
            onError: (err) => {
                showNotification('error', err?.response?.data?.error || i18n.t(`${T_PREFIX}.notifications.update_error`));
            }
        }
    );

    // Handlers
    const showNotification = (type, title) => {
        setNotification({ show: true, type, title });
        setTimeout(() => setNotification({ show: false, type: '', title: '' }), 3000);
    };

    const handleOpenCoverages = (method) => {
        history.push(
            LASTMILE_SHIPPING_METHOD_COVERAGES
                .replace(':warehouse_id', warehouse_id)
                .replace(':shipping_method_id', method.id)
        );
    };

    const handleOpenHistory = (method) => {
        setSelectedHistoryMethod(method);
        setIsHistoryModalOpen(true);
    };

    const handleToggleActive = (method) => {
        setMethodToToggle(method);
        setConfirmToggleOpen(true);
    };

    const handleConfirmToggle = () => {
        if (methodToToggle) {
            toggleActiveMutation.mutate(
                { id: methodToToggle.id, is_active: !methodToToggle.is_active },
                {
                    onSettled: () => {
                        setConfirmToggleOpen(false);
                        setMethodToToggle(null);
                    }
                }
            );
        }
    };

    // Filters (same set as before; carrier_name is the dropdown now)
    const filtersInitialData = useMemo(() => [
        {
            key: "shipping_name",
            label: i18n.t(`${T_PREFIX}.filters.shipping_name`),
            data: {
                visible: false,
                type: 'text',
                value: '',
                placeholder: i18n.t(`${T_PREFIX}.filters.shipping_name`)
            }
        },
        {
            key: "carrier_name",
            label: i18n.t(`${T_PREFIX}.filters.carrier_name`),
            data: {
                visible: false,
                type: 'multiFilter',
                value: [],
                placeholder: i18n.t(`${T_PREFIX}.filters.carrier_name`),
                options: carrierNameOptions
            }
        },
        {
            key: "shipping_type",
            label: i18n.t(`${T_PREFIX}.filters.shipping_type`),
            data: {
                visible: false,
                type: 'text',
                value: '',
                placeholder: i18n.t(`${T_PREFIX}.filters.shipping_type`)
            }
        },
        {
            key: "delivery_type",
            label: i18n.t(`${T_PREFIX}.filters.delivery_type`),
            data: {
                visible: false,
                type: 'text',
                value: '',
                placeholder: i18n.t(`${T_PREFIX}.filters.delivery_type`)
            }
        },
        {
            key: "is_active",
            label: i18n.t(`${T_PREFIX}.filters.is_active`),
            data: {
                visible: false,
                type: 'boolean',
                onClickValue: true
            }
        },
        {
            key: "inactive_only",
            label: i18n.t(`${T_PREFIX}.filters.inactive_only`),
            data: {
                visible: false,
                type: 'boolean',
                onClickValue: true
            }
        }
    ], [i18n, carrierNameOptions]);

    const [filtersData, setFiltersData] = useState(filtersInitialData);

    useEffect(() => {
        setFiltersData((prev) =>
            filtersInitialData.map((initial) => {
                const existing = prev.find((f) => f.key === initial.key);
                if (!existing) return initial;
                return {
                    ...initial,
                    data: { ...initial.data, visible: existing.data.visible, value: existing.data.value }
                };
            })
        );
    }, [filtersInitialData]);

    const EXCLUSIVE_FILTERS = { is_active: 'inactive_only', inactive_only: 'is_active' };

    const updateFiltersData = (updatedData, filter) => {
        setFiltersData((prev) => {
            return prev.map((item) => {
                if (item.key === filter) {
                    return { ...item, data: updatedData };
                }
                if (EXCLUSIVE_FILTERS[filter] === item.key && updatedData.visible) {
                    return { ...item, data: { ...item.data, visible: false } };
                }
                return item;
            }).sort((a, b) => (b.data.visible ? 1 : 0) - (a.data.visible ? 1 : 0));
        });
    };

    const handleApplyFilters = () => {
        const newSearchParams = { warehouse_id };
        filtersData.forEach((filter) => {
            if (filter.key === 'inactive_only' && filter.data.visible) {
                newSearchParams['is_active'] = false;
                return;
            }
            const isEmpty = Array.isArray(filter.data.value)
                ? filter.data.value.length === 0
                : !filter.data.value;
            if (filter.data.visible && !isEmpty) {
                newSearchParams[filter.key] = filter.data.value;
            }
        });
        setSearchParams(newSearchParams);
        queryClient.invalidateQueries(['lastmile_shipping_methods']);
    };

    const handleResetFilters = () => {
        setSearchParams({ warehouse_id });
        setFiltersData(filtersInitialData);
    };

    // Format non_working_days for the inline row stat
    const formatNonWorkingDays = useCallback((days) => {
        if (!days || days.length === 0) return '—';
        return [...days].sort((a, b) => a - b).map(d => i18n.t(`lastmile_management.days_short.${d}`)).join(', ');
    }, [i18n]);

    // Group methods by carrier, sort carriers and methods alphabetically
    const carrierGroups = useMemo(() => {
        const methods = data?.shipping_methods || [];
        const byCarrier = methods.reduce((acc, method) => {
            // Group by the REAL carrier: Uber is managed as its own carrier here,
            // never merged under the Cubbo whitelabel (CUBBO-2185).
            const carrier = realCarrierName(method.carrier_name, method.internal_carrier_name) || '—';
            if (!acc[carrier]) acc[carrier] = [];
            acc[carrier].push(method);
            return acc;
        }, {});
        return Object.keys(byCarrier)
            .sort((a, b) => a.localeCompare(b))
            .map((carrier) => {
                const list = byCarrier[carrier].sort((a, b) =>
                    (a.shipping_name || '').localeCompare(b.shipping_name || '')
                );
                return {
                    carrier,
                    methods: list,
                    activeCount: list.filter(m => m.is_active).length
                };
            });
    }, [data?.shipping_methods]);

    if (!hasAccess) {
        return (
            <PageView>
                <div className="text-center py-12">
                    <p className="text-gray-500">{i18n.t(`${T_PREFIX}.errors.no_access`)}</p>
                </div>
            </PageView>
        );
    }

    const renderMethodRow = (method) => (
        <div
            key={method.id}
            role="button"
            tabIndex={0}
            onClick={() => handleOpenCoverages(method)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleOpenCoverages(method); }}
            className="flex cursor-pointer items-center gap-4 px-5 py-4 transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
        >
            {/* Name + numeric ID */}
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-gray-900">
                    {method.shipping_name}
                </div>
                <div className="mt-0.5 truncate font-mono text-xs text-gray-500">
                    ID: {method.id}
                </div>
            </div>

            {/* Type (shipping_type · delivery_type) — label lives in the column header row */}
            <div className="hidden min-w-0 flex-1 truncate text-sm text-gray-900 md:block">
                {[method.shipping_type, method.delivery_type].filter(Boolean).join(' · ') || '—'}
            </div>

            {/* Non-working days — label lives in the column header row */}
            <div className="hidden w-40 shrink-0 truncate text-sm text-gray-900 lg:block">
                {formatNonWorkingDays(method.non_working_days)}
            </div>

            {/* Status pill — fixed-width container so the day column doesn't shift row-to-row */}
            <div className="w-28 shrink-0">
                <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${
                        method.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                >
                    <span className={`h-1.5 w-1.5 rounded-full ${method.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                    {method.is_active
                        ? i18n.t(`${T_PREFIX}.status_active`)
                        : i18n.t(`${T_PREFIX}.status_inactive`)}
                </span>
            </div>

            {/* Inline actions — also fixed width, do not propagate clicks to the row */}
            {canEdit && (
                <div className="flex w-16 shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                        onClick={() => handleOpenHistory(method)}
                        className="p-1 text-gray-400 transition-colors hover:text-gray-700"
                        title={i18n.t(`${T_PREFIX}.history.title`)}
                    >
                        <ClockIcon className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => handleToggleActive(method)}
                        className="p-1 text-gray-400 transition-colors hover:text-cubbo-700"
                        title={method.is_active ? i18n.t(`${T_PREFIX}.actions.deactivate`) : i18n.t(`${T_PREFIX}.actions.activate`)}
                        disabled={toggleActiveMutation.isLoading}
                    >
                        {method.is_active
                            ? <BanIcon className="h-5 w-5" />
                            : <CheckCircleIcon className="h-5 w-5" />}
                    </button>
                </div>
            )}

            <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-300" />
        </div>
    );

    return (
        <>
            <Notification
                show={notification.show}
                setShow={(val) => setNotification(prev => ({ ...prev, show: val }))}
                type={notification.type}
                title={notification.title}
            />

            <PageView
                topMenu={
                    <div className="flex items-center" style={{ height: '72px' }}>
                        <div className="flex flex-col leading-tight">
                            <div className="text-lg font-semibold text-gray-900">
                                {i18n.t(`${T_PREFIX}.title`)}
                            </div>
                            <div className="text-sm text-gray-500">
                                {i18n.t(`${T_PREFIX}.subtitle`)}
                            </div>
                        </div>
                    </div>
                }
                childrenFullWidth={true}
                topMenuFullWidth={true}
            >
                {isLoading ? (
                    <Loader show={true} />
                ) : isError ? (
                    <div className="text-center py-12">
                        <p className="text-red-500">
                            {error?.message || i18n.t(`${T_PREFIX}.errors.fetch_error`)}
                        </p>
                    </div>
                ) : (
                    <div className="mx-auto max-w-7xl px-4 py-6">
                        {/* Filters — single white rail same width as the cards below */}
                        <div className="mb-4 rounded-lg bg-white py-3 shadow-sm">
                            <Filter
                                filtersData={filtersData}
                                onChange={updateFiltersData}
                                resetAll={handleResetFilters}
                                applyFiltersManually={true}
                                onManuallyAppliedFilters={handleApplyFilters}
                            />
                        </div>

                        {carrierGroups.length === 0 ? (
                            <div className="py-16 text-center">
                                <p className="text-gray-500">
                                    {i18n.t(`${T_PREFIX}.empty_table`)}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {carrierGroups.map(({ carrier, methods, activeCount }) => (
                                    <section
                                        key={carrier}
                                        className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                                    >
                                        {/* Carrier header */}
                                        <header className="flex items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
                                            <div className="flex min-w-0 items-center gap-3">
                                                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white ${carrierBadgeColor(carrier)}`}>
                                                    {carrierInitials(carrier)}
                                                </div>
                                                <h2 className="truncate text-lg font-semibold text-gray-900">
                                                    {carrier}
                                                </h2>
                                            </div>
                                            <div className="shrink-0 text-right">
                                                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                                    {i18n.t(`${T_PREFIX}.methods_active`)}
                                                </div>
                                                <div className="mt-0.5 text-sm text-gray-900">
                                                    <span className="font-medium">{activeCount}</span>
                                                    <span className="text-gray-300"> / </span>
                                                    <span>{methods.length}</span>
                                                </div>
                                            </div>
                                        </header>

                                        {/* Column headers — widths must match renderMethodRow exactly */}
                                        <div className="flex items-center gap-4 border-b border-gray-100 bg-gray-50 px-5 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                                            <div className="min-w-0 flex-1">
                                                {i18n.t(`${T_PREFIX}.column_title`)}
                                            </div>
                                            <div className="hidden min-w-0 flex-1 md:block">
                                                {i18n.t(`${T_PREFIX}.column_type`)}
                                            </div>
                                            <div className="hidden w-40 shrink-0 lg:block">
                                                {i18n.t(`${T_PREFIX}.headers.non_working_days`)}
                                            </div>
                                            <div className="w-28 shrink-0" aria-hidden="true" />
                                            {canEdit && <div className="w-16 shrink-0" aria-hidden="true" />}
                                            <div className="h-5 w-5 shrink-0" aria-hidden="true" />
                                        </div>

                                        {/* Method rows */}
                                        <div className="divide-y divide-gray-100">
                                            {methods.map(renderMethodRow)}
                                        </div>
                                    </section>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </PageView>

            <ShippingMethodHistoryModal
                open={isHistoryModalOpen}
                setOpen={setIsHistoryModalOpen}
                methodData={selectedHistoryMethod}
                warehouseTimeZone={warehouse?.time_zone}
            />

            <ConfirmDialog
                open={confirmToggleOpen}
                setOpen={setConfirmToggleOpen}
                title={methodToToggle?.is_active
                    ? i18n.t(`${T_PREFIX}.actions.deactivate`)
                    : i18n.t(`${T_PREFIX}.actions.activate`)
                }
                description={methodToToggle?.is_active
                    ? i18n.t(`${T_PREFIX}.actions.confirm_deactivate`)
                    : i18n.t(`${T_PREFIX}.actions.confirm_activate`)
                }
                confirmLabel={methodToToggle?.is_active
                    ? i18n.t(`${T_PREFIX}.actions.deactivate`)
                    : i18n.t(`${T_PREFIX}.actions.activate`)
                }
                cancelLabel={i18n.t(`${T_PREFIX}.form.cancel`)}
                onConfirm={handleConfirmToggle}
                onCancel={() => {
                    setConfirmToggleOpen(false);
                    setMethodToToggle(null);
                }}
                loading={toggleActiveMutation.isLoading}
                type={methodToToggle?.is_active ? 'danger' : 'default'}
            />
        </>
    );
};
